import {OdontoBookingEnum} from 'presentation/constants';
import {BookingTypeEnum} from 'presentation/modules/booking/shared/constants/bookingTypeEnum';
import {FlowEnum} from 'presentation/modules/booking/shared/constants/flowEnum';
import {SUGGESTED_SPECIALTY} from 'presentation/modules/booking/shared/constants/suggestedSpecialty';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useQueryParams} from 'presentation/pages';
import {useGetHomeQuery} from 'presentation/pages/private/home/query/useGetHomeQuery';
import {ParsedOdontoAppointment} from 'presentation/pages/private/home/utils/parseOdontoAppointments/types';
import {useAccountDataStore} from 'presentation/pages/private/store/useAccountDataStateStore';
import {translations} from 'presentation/translations-new';
import {Specialty} from 'presentation/utils';
import {getSpecialtyBySearch} from 'presentation/utils/GetSpecialtyBySearch';
import {isUnprocessableError} from 'presentation/utils/isUnprocessableError';
import {textHandle} from 'presentation/utils/textHandle';
import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useGetOdontoSpecialtiesQuery} from '../../queries/useGetOdontoSpecialtiesQuery';
import {CheckBookingModal, CheckBookingModalEnum, UseOdontoSpecialtySelectionProps} from './types';

const {verifyingAuth} = translations['pt-br'].privateFlow.bookingFlow.specialtyFlow;

const {loading} = translations['pt-br'].commons;

export function useOdontoSpecialtySelection({handleNextStep}: UseOdontoSpecialtySelectionProps) {
	const {
		SEARCH_PARAMS: {query, searchByLetter},
		clearSearch,
	} = useQueryParams();
	const {
		data: specialtiesList,
		isError: specialtiesListError,
		isFetching,
		refetch,
	} = useGetOdontoSpecialtiesQuery();
	const resetBookingData = useBookingStore(state => state.resetBookingData);
	const setBookingData = useBookingStore(state => state.setBookingData);
	const navigate = useNavigate();
	const {
		beneficiary: {idadeC},
	} = useAccountDataStore(state => state.accountData);
	const [checkBookingModal, setCheckBookingModal] = useState<CheckBookingModal>('none');
	const [scheduledBooking, setScheduledBooking] = useState<ParsedOdontoAppointment | null>(null);
	const {refetch: refetchAppointmentsList} = useGetHomeQuery();

	const specialtiesListHasUnprocessableError = isUnprocessableError(specialtiesListError);
	const searchTerm = textHandle(query).sanitizeText();

	const searchFilteredByTerms = getSpecialtyBySearch({
		text: searchTerm,
		flowType: FlowEnum.ODONTO,
	});

	const FILTERED_LIST_BY_CATEGORY = specialtiesList?.map(specialty => ({
		...specialty,
		list: specialty?.list?.filter(item => {
			const formattedText = textHandle(item?.formattedText).sanitizeText();
			const termsRegex = new RegExp(`\\b${searchFilteredByTerms}\\b`, 'i');

			return formattedText.includes(searchTerm) || termsRegex.test(formattedText);
		}),
	}));

	const FILTERED_LIST_BY_LETTER = FILTERED_LIST_BY_CATEGORY?.filter(
		item => item?.category === searchByLetter,
	);

	const LIST = searchByLetter ? FILTERED_LIST_BY_LETTER : FILTERED_LIST_BY_CATEGORY;

	const showNotFoundState =
		!specialtiesList || specialtiesList.length === 0 || specialtiesListHasUnprocessableError;

	const showEmptyState = LIST?.every(categoryItem => categoryItem?.list?.length === 0);

	const isPediatric = idadeC < 18;

	const suggestionSpecialty = specialtiesList
		?.flatMap(category => category.list)
		.filter(
			specialty =>
				specialty?.code ===
				(isPediatric
					? SUGGESTED_SPECIALTY.generalPediatricOdonto.code
					: SUGGESTED_SPECIALTY.generalAdultOdonto.code),
		)?.[0] as Specialty<FlowEnum.ODONTO>;

	const hideSuggestionSpecialty = Boolean(searchByLetter || !suggestionSpecialty);

	const loadingText =
		checkBookingModal === CheckBookingModalEnum.VERIFYING_AUTH ? verifyingAuth : loading;

	const handleNavToStateSelection = () => {
		clearSearch();
		resetBookingData();
		navigate(OdontoBookingEnum.ODONTO_BOOKING_UF);
	};

	const handleSelectCard = async (specialty: Specialty) => {
		const hasSubSpecialty = 'sub_code' in specialty && specialty.sub_code;

		try {
			setCheckBookingModal('verifyingDuplicity');
			const {data: updatedAppointmentsList} = await refetchAppointmentsList();
			const alreadyScheduled = updatedAppointmentsList?.appointments?.find(
				booking =>
					booking.specialtyCode === specialty.code &&
					booking.bookingType === BookingTypeEnum.ODONTO,
			);
			setCheckBookingModal('none');

			if (alreadyScheduled) {
				setScheduledBooking({
					...alreadyScheduled,
					subSpecialtyCode: hasSubSpecialty ? specialty.sub_code : '',
					specialtyNameSelected: specialty.formattedText,
				} as ParsedOdontoAppointment);
				setCheckBookingModal('duplicityState');
				return;
			}

			setBookingData(prev => ({
				...prev,
				specialtyCode: specialty.code,
				specialtyName: specialty.formattedText,
				subSpecialtyCode: hasSubSpecialty ? specialty.sub_code : '',
				professionalCode: '',
				professionalName: '',
				unitCode: '',
				unitName: '',
				unitAddress: '',
				dateAt: '',
				hourAt: '',
				districtName: '',
				odontoRecommendations: '',
			}));

			clearSearch();
			handleNextStep();
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error(error);
			setCheckBookingModal('error');
		}
	};

	const resetBookingDataSpecialties = () => {
		setBookingData(prev => ({
			...prev,
			specialtyCode: '',
			subSpecialtyCode: undefined,
		}));
	};

	return {
		isFetching,
		specialtiesListError,
		specialtiesListHasUnprocessableError,
		refetch,
		showNotFoundState,
		showEmptyState,
		query,
		handleNavToStateSelection,
		specialtiesList: LIST,
		suggestionSpecialty,
		handleSelectCard,
		checkBookingModal,
		scheduledBooking,
		hideSuggestionSpecialty,
		loadingText,
		resetBookingDataSpecialties,
		setCheckBookingModal,
	};
}
